import { useQueryClient } from '@tanstack/react-query'
import Button from 'ext-ui/components/Button/Button'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import Pill from 'ext-ui/components/Pill/Pill'
import UploadCloudIcon from 'ext-ui/svg/icon/UploadCloudIcon'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectTransactionIdFromPathState } from 'src/app/TransactionDetail/redux/transactionDetail.selectors'
import AttachmentListRows from 'src/app/TransactionDetail/Sections/AttachmentListRows/AttachmentListRows'
import WideDropzone from 'src/app/TransactionDetail/Sections/AttachmentListRows/WideDropzone'
import ReceiptRecoverySection, {
  ReceiptRecoveryReviewOutcome,
} from 'src/app/TransactionDetail/Sections/ReceiptRecoverySection'
import ErrorList from 'src/components/ErrorList/ErrorList'
import ReceiptsVerificationSummary from 'src/components/ReceiptsVerificationSummary/ReceiptsVerificationSummary'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { ExtendCurrency, OrganizationConnectedPlatform } from 'src/enums'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { useNullSafeLocale } from 'src/localization/useNullSafeLocale'
import { OrganizationReceiptRulesResponse } from 'src/models/organizations.models'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { statusToVerificationSummary } from 'src/models/receiptVerification.models'
import {
  injectMockTransactionVerification,
  mockTransactionVerificationStatus,
} from 'src/models/receiptVerificationMock'
import {
  ConnectedPlatformStatus,
  ReceiptRecoveryCase,
  ReceiptRecoveryDisplayCase,
  ReceiptRecoveryLifecycleState,
  TransactionDetailModel,
} from 'src/models/transaction.models'
import { useQueryActiveTransactionIntegration } from 'src/queries/integrations/orgIntegrations.queries'
import { useReceiptVerificationEnabled } from 'src/queries/organizations/receiptVerification.queries'
import { useHasSaasFeatures } from 'src/queries/organizations/saas.queries'
import { usePushERPReceiptAttachments } from 'src/queries/receiptAttachments/receiptAttachments.queries'
import { updateTransactionDetailResponse } from 'src/queries/transactions.queries'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { PatchTransactionRequest } from 'src/requests/patchTransaction.request'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { toDollars } from 'src/utils/currency.utils'

import {
  defaultReceiptRecoveryMockPreviewState,
  mergeReceiptRecoveryAttachments,
  ReceiptRecoveryMockPreviewState,
} from '../receiptRecoveryPreview'
import style, {
  syncErrorStyle,
  uploadErrorStyle,
} from './ReceiptsSection.style'

interface ReceiptsSectionProps {
  transaction: TransactionDetailModel
  /** Local design preview may supply a case before the API has one. */
  receiptRecoveryCase?: ReceiptRecoveryCase | null
  /** Authorized detached candidate returned by the Receipt Agent case endpoint. */
  recoveredReceiptAttachment?: ReceiptAttachmentModel
  /** Enables the non-persisting review control for local Receipt Agent preview data. */
  isMockReceiptRecoveryPreview?: boolean
  /** Selects the initial local Receipt Agent preview state. */
  mockReceiptRecoveryPreviewState?: ReceiptRecoveryMockPreviewState
  /** Lets the feature-gated flyout own mock preview navigation. */
  onMockReceiptRecoveryPreviewStateChange?: (
    state: ReceiptRecoveryMockPreviewState,
  ) => void
  /** Lets the feature-gated flyout advance its mock review state. */
  onMockReceiptRecoveryReview?: (outcome: ReceiptRecoveryReviewOutcome) => void
  /** Invokes the live Receipt Agent decision mutation outside local preview mode. */
  onSubmitReceiptRecoveryReview?: (
    outcome: ReceiptRecoveryReviewOutcome,
  ) => void
  isSubmittingReceiptRecoveryReview?: boolean
  receiptRecoveryReviewError?: string
  className?: string
  attachments: ReceiptAttachmentModel[]
  deleteAttachment?: (attachmentId: string) => void
  topLevelError?: string
  uploadAttachments?: (attachments: PostReceiptAttachmentRequest[]) => void
  uploadingAttachmentThumbnails?: Record<string, string>
  uploadingAttachmentFileNames?: Record<string, string>
  failedUploadThumbnails?: Record<string, string>
  failedUploadFileNames?: Record<string, string>
  recentlyUploadedAttachmentIds?: Set<string>
  isReceiptRequired: boolean
  errors?: boolean
  isReceiptSectionVisible?: boolean
  patchTransaction?: (data: Partial<PatchTransactionRequest>) => void
  receiptRule?: OrganizationReceiptRulesResponse
}

function formatRecoveryDate(
  timestamp: string | undefined,
  locale: string,
): string | undefined {
  if (!timestamp) return undefined

  const date = new Date(timestamp)
  if (Number.isNaN(date.valueOf())) return undefined

  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}

const ReceiptsSection: React.FC<ReceiptsSectionProps> = ({
  transaction,
  receiptRecoveryCase: receiptRecoveryCaseOverride,
  recoveredReceiptAttachment,
  isMockReceiptRecoveryPreview = false,
  mockReceiptRecoveryPreviewState = defaultReceiptRecoveryMockPreviewState,
  onMockReceiptRecoveryPreviewStateChange,
  onMockReceiptRecoveryReview,
  onSubmitReceiptRecoveryReview,
  isSubmittingReceiptRecoveryReview = false,
  receiptRecoveryReviewError,
  attachments,
  deleteAttachment,
  topLevelError,
  uploadAttachments,
  uploadingAttachmentThumbnails,
  uploadingAttachmentFileNames,
  failedUploadThumbnails,
  failedUploadFileNames,
  recentlyUploadedAttachmentIds,
  isReceiptRequired,
  errors,
  isReceiptSectionVisible,
  patchTransaction,
  receiptRule,
}): React.ReactElement => {
  const translate = useTranslate()
  const locale = useNullSafeLocale()
  const currencyFormatter = useCurrencyFormatter()

  const queryClient = useQueryClient()

  const { analytics } = useAnalyticsContext()
  const [hasErrors, setHasErrors] = useState(false)
  const [receiptRequired, setReceiptRequired] = useState(isReceiptRequired)
  const [attachmentError, setAttachmentError] = useState<
    string | React.ReactElement | undefined
  >()
  const transactionId = useSelector(selectTransactionIdFromPathState)
  const { data: activeTransactionIntegration } =
    useQueryActiveTransactionIntegration(transactionId)
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const user = useCurrentUser()
  const pushERPReceiptAttachments = usePushERPReceiptAttachments()
  const isPushingToERP = pushERPReceiptAttachments.isPending
  const verificationUiEnabled = useReceiptVerificationEnabled()
  const hasSaasFeatures = useHasSaasFeatures()

  const attachmentsForRows = useMemo(() => {
    if (!verificationUiEnabled) return attachments
    const enriched = injectMockTransactionVerification({
      id: transaction?.id,
      attachmentsCount: attachments.length,
      attachments,
      receiptMatchingStatus: transaction?.receiptMatchingStatus,
    })
    return enriched.attachments ?? []
  }, [
    verificationUiEnabled,
    transaction?.id,
    transaction?.receiptMatchingStatus,
    attachments,
  ])

  const recoveryAttachmentsForRows = useMemo(
    () =>
      mergeReceiptRecoveryAttachments(
        attachmentsForRows,
        recoveredReceiptAttachment,
      ),
    [attachmentsForRows, recoveredReceiptAttachment],
  )

  const verificationSummary = useMemo(() => {
    const status =
      transaction?.receiptMatchingStatus ??
      mockTransactionVerificationStatus(transaction?.id, attachments.length)
    return statusToVerificationSummary(status)
  }, [transaction?.id, transaction?.receiptMatchingStatus, attachments.length])

  const syncedWithIntegration = useMemo(
    () =>
      activeTransactionIntegration !== undefined &&
      activeTransactionIntegration.syncStatus ===
        ConnectedPlatformStatus.Success,
    [activeTransactionIntegration],
  )

  const syncAttachmentsError = useMemo(() => {
    return (
      attachments.find((a) => {
        const connection = a.connectedPlatforms?.find(
          (connection) =>
            connection.platform === activeIntegration?.connectedPlatform,
        )
        return (
          connection !== undefined &&
          connection.syncStatus === ConnectedPlatformStatus.Failed
        )
      }) !== undefined
    )
  }, [attachments])

  const attachmentsSyncedCount = useMemo(() => {
    return attachments.filter((a) => {
      const connection = a.connectedPlatforms?.find(
        (connection) =>
          connection.platform === activeIntegration?.connectedPlatform,
      )
      return (
        connection !== undefined &&
        connection.syncStatus === ConnectedPlatformStatus.Success
      )
    }).length
  }, [attachments])

  const erpStatusByAttachmentId = useMemo(() => {
    if (!activeIntegration) return undefined
    const map: Record<string, ConnectedPlatformStatus> = {}
    for (const a of attachments) {
      const connection = a.connectedPlatforms?.find(
        (cp) => cp.platform === activeIntegration.connectedPlatform,
      )
      if (connection) map[a.id] = connection.syncStatus
    }
    return map
  }, [attachments, activeIntegration])

  const receiptRecoveryCaseFromData =
    receiptRecoveryCaseOverride === undefined
      ? transaction.receiptRecoveryCase
      : receiptRecoveryCaseOverride
  const receiptRecoveryCase = receiptRecoveryCaseFromData?.lifecycleState
    ? (receiptRecoveryCaseFromData as ReceiptRecoveryDisplayCase)
    : undefined

  const recoveredReceipt = receiptRecoveryCase?.recoveredAttachmentId
    ? {
        attachmentId: receiptRecoveryCase.recoveredAttachmentId,
        recoveredFileName: receiptRecoveryCase.recoveredFileName,
        recoveredFrom: receiptRecoveryCase.recoveredFrom,
        recoveredAtLabel: formatRecoveryDate(
          receiptRecoveryCase.recoveredReceipt?.submittedAt ??
            receiptRecoveryCase.lastAgentRunAt,
          locale,
        ),
      }
    : undefined
  const setErrors = (onlyOneReceipt: boolean): void => {
    if (errors) {
      setHasErrors(onlyOneReceipt)
    }
  }

  const handleTrackReceiptRulesEnabled = (enabled: boolean): void => {
    analytics?.track('Transactions - Modify Receipt rule - button press', {
      modification_type: enabled ? 'enabled' : 'disabled',
    })
  }
  const handleCheckboxClick = (): void => {
    setReceiptRequired(!receiptRequired)
    patchTransaction?.({
      receiptRulesEnabled: !receiptRequired,
    })
    handleTrackReceiptRulesEnabled(!receiptRequired)
  }

  const syncAttachments = (): void => {
    if (
      !isPushingToERP &&
      activeIntegration !== undefined &&
      activeTransactionIntegration !== undefined
    ) {
      pushERPReceiptAttachments
        .mutateAsync({
          orgId: activeIntegration.organizationId,
          connectionId: activeIntegration.id,
          transactionId: activeTransactionIntegration.transactionId,
        })
        .then((raConnectedPlatforms) => {
          const lookup = new Set(
            raConnectedPlatforms.map((ra) => ra.receiptAttachmentId),
          )
          updateTransactionDetailResponse(queryClient, {
            transactionId,
            updater: (old) => ({
              ...old,
              receiptAttachments: old.receiptAttachments.map((ra) =>
                lookup.has(ra.id)
                  ? {
                      ...ra,
                      connectedPlatforms: raConnectedPlatforms.filter(
                        (cp) => cp.receiptAttachmentId === ra.id,
                      ),
                    }
                  : ra,
              ),
            }),
          })
        })
        .catch(() => {
          setAttachmentError(translate('Failed to sync, please try again'))
        })
      analytics?.track('sync transactions attachments to ERP', {
        count: attachments.length - attachmentsSyncedCount,
      })
    }
  }

  const receiptVerificationSummary =
    verificationUiEnabled && hasSaasFeatures ? (
      <ReceiptsVerificationSummary summary={verificationSummary} />
    ) : undefined

  const mockReceiptRecoveryHasPushableReceipt =
    receiptRecoveryCase?.lifecycleState ===
      ReceiptRecoveryLifecycleState.NeedsReview ||
    receiptRecoveryCase?.lifecycleState ===
      ReceiptRecoveryLifecycleState.ReceiptReconciled

  const canPushToERP =
    isMockReceiptRecoveryPreview ||
    (syncedWithIntegration &&
      activeIntegration &&
      activeIntegration.connectedPlatform !==
        OrganizationConnectedPlatform.QUICKBOOKS_DESKTOP &&
      user?.hasOrgBookkeeperPermissions)

  const pushToERPButton = canPushToERP ? (
    <Button
      variant={'no-shell-no-padding'}
      dropShadow={false}
      loading={isMockReceiptRecoveryPreview ? false : isPushingToERP}
      iconLeft={<UploadCloudIcon />}
      onClick={isMockReceiptRecoveryPreview ? undefined : syncAttachments}
      disabled={
        isMockReceiptRecoveryPreview
          ? !mockReceiptRecoveryHasPushableReceipt
          : attachments.length <= 0 ||
            isPushingToERP ||
            attachmentsSyncedCount === attachments.length
      }
    >
      {translate(
        isPushingToERP
          ? 'Pushing to ${integration}...'
          : 'Push to ${integration}',
        {
          integration: isMockReceiptRecoveryPreview
            ? 'QuickBooks'
            : activeIntegration?.platformName || '',
        },
      )}
    </Button>
  ) : undefined

  const renderAttachmentListRows = (
    isMissing: boolean,
    displayAttachments = attachmentsForRows,
    useInlineAddReceipt = false,
    attachmentCountForUploadLimit: number | undefined = undefined,
    includeUploader = true,
    recoveredReceiptForRows: typeof recoveredReceipt = undefined,
    showViewingLabel = false,
  ): React.ReactElement => (
    <AttachmentListRows
      attachments={displayAttachments}
      uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
      uploadingAttachmentFileNames={uploadingAttachmentFileNames}
      failedUploadThumbnails={failedUploadThumbnails}
      failedUploadFileNames={failedUploadFileNames}
      recentlyUploadedAttachmentIds={recentlyUploadedAttachmentIds}
      uploadAttachments={includeUploader ? uploadAttachments : undefined}
      deleteAttachment={deleteAttachment}
      setAttachmentError={setAttachmentError}
      setErrors={setErrors}
      integrationName={activeIntegration?.platformName}
      integrationLogoSrc={activeIntegration?.logo}
      erpStatusByAttachmentId={erpStatusByAttachmentId}
      showVerificationStatus={verificationUiEnabled && hasSaasFeatures}
      isMissing={isMissing}
      useInlineAddReceipt={useInlineAddReceipt}
      attachmentCountForUploadLimit={attachmentCountForUploadLimit}
      showViewingLabel={showViewingLabel}
      recoveredReceipt={recoveredReceiptForRows}
    />
  )

  const renderRecoveryUploader = (isMissing: boolean): React.ReactElement => (
    <>
      {syncAttachmentsError && (
        <span css={syncErrorStyle}>
          {translate(
            'One or more receipts failed to push. This could be due to the attachment being in an unsupported image format or network errors. Please change the image format or try again later.',
          )}
        </span>
      )}
      <div className={'ReceiptsSection content'}>
        {uploadAttachments && (
          <WideDropzone
            uploadAttachments={uploadAttachments}
            setAttachmentError={setAttachmentError}
            setErrors={setErrors}
            attachmentsLength={attachmentsForRows.length}
            isMissing={isMissing}
            useInlineAddReceipt={true}
          />
        )}
        {renderReceiptFooter()}
      </div>
    </>
  )

  const renderReceiptFooter = (): React.ReactElement => (
    <>
      {(attachmentError ||
        hasErrors ||
        topLevelError ||
        receiptRecoveryReviewError) && (
        <ErrorList
          css={uploadErrorStyle}
          errors={[
            ...(attachmentError !== undefined ? [attachmentError] : []),
            ...(hasErrors
              ? [translate('At least one receipt must be attached')]
              : []),
            ...(topLevelError !== undefined ? [topLevelError] : []),
            ...(receiptRecoveryReviewError !== undefined
              ? [receiptRecoveryReviewError]
              : []),
          ].filter((i) => i !== undefined)}
        />
      )}
      {isReceiptSectionVisible && (
        <div className={'receipt-requirement-checkbox'}>
          <div className={'checkbox-and-label'}>
            <Checkbox
              aria-label={translate('Enable Receipt Rules Checkbox')}
              onChange={handleCheckboxClick}
              checked={receiptRequired}
              data-testid={`enableReceiptRules`}
              data-is-checked={receiptRequired}
            />
            <span className={'font-form-label text-primary'}>
              {translate('Enable receipt rules for this transaction')}
            </span>
          </div>
          <span className={'font-sm text-secondary checkbox-subcopy'}>
            {translate(
              'Receipts are required for transactions of at least ${amount}',
              {
                amount:
                  receiptRule?.usdThresholdCents !== undefined &&
                  receiptRule?.cadThresholdCents !== undefined
                    ? translate('${usThreshold} and ${cadThreshold}', {
                        usThreshold: currencyFormatter({
                          amount: toDollars(receiptRule.usdThresholdCents),
                          currency: ExtendCurrency.Usd,
                          forceShowCurrency: true,
                        }),
                        cadThreshold: currencyFormatter({
                          amount: toDollars(receiptRule.cadThresholdCents),
                          currency: ExtendCurrency.Cad,
                          forceShowCurrency: true,
                        }),
                      })
                    : currencyFormatter({
                        amount: toDollars(
                          receiptRule?.usdThresholdCents !== undefined
                            ? receiptRule.usdThresholdCents
                            : (receiptRule?.cadThresholdCents ?? 0),
                        ),
                        currency:
                          receiptRule?.usdThresholdCents !== undefined
                            ? ExtendCurrency.Usd
                            : ExtendCurrency.Cad,
                      }),
              },
            )}
          </span>
        </div>
      )}
    </>
  )

  return (
    <div css={style} className={'ReceiptsSection'}>
      {receiptRecoveryCase ? (
        <ReceiptRecoverySection
          receiptRecoveryCase={receiptRecoveryCase}
          attachmentList={renderAttachmentListRows(
            false,
            recoveryAttachmentsForRows,
            false,
            undefined,
            false,
            recoveredReceipt,
            true,
          )}
          isMockPreview={isMockReceiptRecoveryPreview}
          mockPreviewState={
            isMockReceiptRecoveryPreview
              ? mockReceiptRecoveryPreviewState
              : undefined
          }
          onMockPreviewStateChange={
            isMockReceiptRecoveryPreview
              ? onMockReceiptRecoveryPreviewStateChange
              : undefined
          }
          onSubmitReview={
            isMockReceiptRecoveryPreview
              ? onMockReceiptRecoveryReview
              : onSubmitReceiptRecoveryReview
          }
          isSubmittingReview={
            isMockReceiptRecoveryPreview
              ? false
              : isSubmittingReceiptRecoveryReview
          }
          headerSupplement={receiptVerificationSummary}
          headerAction={pushToERPButton}
          renderUploader={renderRecoveryUploader}
        />
      ) : (
        <>
          <div className={'flex-row justify-between items-center'}>
            <div className={'header'}>
              <span className={'font-h3'}>{translate('Receipts')}</span>
              {attachments.length === 0 && receiptRequired && (
                <Pill size={'small'} color={'severe'}>
                  {translate('Missing')}
                </Pill>
              )}
              {receiptVerificationSummary}
            </div>
            {pushToERPButton}
          </div>
          {syncAttachmentsError && (
            <span css={syncErrorStyle}>
              {translate(
                'One or more receipts failed to push. This could be due to the attachment being in an unsupported image format or network errors. Please change the image format or try again later.',
              )}
            </span>
          )}
          <div className={'ReceiptsSection content'}>
            {renderAttachmentListRows(
              attachments.length === 0 && receiptRequired,
            )}
            {renderReceiptFooter()}
          </div>
        </>
      )}
    </div>
  )
}

export default ReceiptsSection
